import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faScrewdriverWrench,
    faCircleCheck,
    faTriangleExclamation,
    faSpinner,
    faCalendarPlus,
    faBan,
    faShieldHalved,
    faPowerOff,
    faRotate,
    faCircleInfo,
    faPlugCircleXmark,
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { Config } from 'config/envConfig';
import { getUrl } from '../../pdfDisplay/utils';
import { Button, Input, alert, AlertType } from '@quyva/shared-ui';

const NODE_URL = getUrl(
    `http://${Config.NODE_SERVER_HOST}:${Config.NODE_SERVER_PORT}`
);

function fmtDateTime(iso) {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        dateStyle: 'medium',
        timeStyle: 'short',
    }) + ' IST';
}

function minDateTimeLocal() {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    d.setSeconds(0, 0);
    return d.toISOString().slice(0, 16);
}

function StatusBadge({ status }) {
    const map = {
        scheduled: { color: 'text-amber-600 bg-amber-50 border-amber-200', label: 'Scheduled' },
        active:    { color: 'text-red-600 bg-red-50 border-red-200',       label: 'Active — Traffic Blocked' },
        completed: { color: 'text-green-600 bg-green-50 border-green-200', label: 'Completed' },
        cancelled: { color: 'text-gray-500 bg-gray-50 border-gray-200',    label: 'Cancelled' },
    };
    const cfg = map[status] || map.scheduled;
    return (
        <span className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-semibold ${cfg.color}`}>
            {cfg.label}
        </span>
    );
}

function InfoRow({ label, value }) {
    return (
        <div className='flex items-start justify-between py-2.5 border-b border-gray-100 last:border-0 text-sm gap-4'>
            <span className='text-texts-secondary300 font-medium shrink-0'>{label}</span>
            <span className='text-gray-800 font-semibold text-right'>{value}</span>
        </div>
    );
}

export const MaintenanceSettings = ({ SettingSections, maintenanceRef }) => {
    const user = JSON.parse(localStorage.getItem('current'));
    const authHeader = () => ({ Authorization: encodeURIComponent(user?.token || '') });

    const [status, setStatus]           = useState(null);
    const [loading, setLoading]         = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [formStart, setFormStart]     = useState('');
    const [formDuration, setFormDuration] = useState(60);
    const [formNote, setFormNote]       = useState('');

    const pollRef = useRef(null);

    const fetchStatus = useCallback(async () => {
        try {
            const res = await axios.get(`${NODE_URL}/api/maintenance/status`, {
                headers: authHeader(),
            });
            setStatus(res.data.data);
        } catch (e) {
            alert(AlertType.ERROR, e?.response?.data?.error || 'Failed to load maintenance status');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchStatus();
        pollRef.current = setInterval(fetchStatus, 30000);
        return () => clearInterval(pollRef.current);
    }, [fetchStatus]);

    async function handleSchedule(e) {
        e.preventDefault();
        if (!formStart) { alert(AlertType.ERROR, 'Please select a start date and time.'); return; }
        setActionLoading(true);
        try {
            await axios.post(
                `${NODE_URL}/api/maintenance/schedule`,
                { maintenanceStart: new Date(formStart).toISOString(), durationMinutes: formDuration, note: formNote },
                { headers: { ...authHeader(), 'Content-Type': 'application/json' } }
            );
            alert(AlertType.SUCCESS, 'Maintenance scheduled — emails sent to all users.');
            setFormStart(''); setFormDuration(60); setFormNote('');
            await fetchStatus();
        } catch (e) {
            alert(AlertType.ERROR, e?.response?.data?.error || 'Failed to schedule maintenance');
        } finally {
            setActionLoading(false);
        }
    }

    async function handleCancel() {
        if (!window.confirm('Cancel this maintenance window? Files are removed silently — no user email sent.')) return;
        setActionLoading(true);
        try {
            await axios.delete(`${NODE_URL}/api/maintenance/cancel`, { headers: authHeader() });
            alert(AlertType.SUCCESS, 'Maintenance cancelled and files removed.');
            await fetchStatus();
        } catch (e) {
            alert(AlertType.ERROR, e?.response?.data?.error || 'Failed to cancel maintenance');
        } finally {
            setActionLoading(false);
        }
    }

    async function handleRemovePage() {
        if (!window.confirm('Remove the maintenance page? Caddy will immediately start serving traffic again. Only do this when all services are healthy and ready.')) return;
        setActionLoading(true);
        try {
            await axios.delete(`${NODE_URL}/api/maintenance/page`, { headers: authHeader() });
            alert(AlertType.SUCCESS, 'Maintenance page removed — traffic restored.');
            await fetchStatus();
        } catch (e) {
            alert(AlertType.ERROR, e?.response?.data?.error || 'Failed to remove maintenance page');
        } finally {
            setActionLoading(false);
        }
    }

    async function handleConfirmShutdown() {
        setActionLoading(true);
        try {
            const res = await axios.post(
                `${NODE_URL}/api/maintenance/confirm-shutdown`, {},
                { headers: authHeader() }
            );
            const { processingJobCount, safe } = res.data;
            setStatus(prev => prev ? { ...prev, processingJobCount } : prev);
            if (safe) {
                alert(AlertType.SUCCESS, 'All jobs complete — safe-to-restart email sent to super-admins.');
            } else {
                alert(AlertType.ERROR, `${processingJobCount} job(s) still processing — server restart is NOT safe yet.`);
            }
        } catch (e) {
            alert(AlertType.ERROR, e?.response?.data?.error || 'Safety check failed');
        } finally {
            setActionLoading(false);
        }
    }

    const sched    = status?.schedule;
    const isActive = status?.maintenanceActive;
    const hasWindow = sched && (sched.status === 'scheduled' || sched.status === 'active');
    const jobCount  = status?.processingJobCount;  // null = not checked, 0 = safe, >0 = blocking

    return (
        <div className='mb-6'>
            {/* ─── Section header ─────────────────────────────────────────── */}
            <div
                ref={maintenanceRef}
                className='flex items-center justify-between font-semibold mb-6 py-3 px-4 text-texts-secondary300 uppercase bg-backgrounds-primary100 truncate min-h-[60px]'
            >
                <div className='flex items-center gap-2'>
                    <FontAwesomeIcon icon={faScrewdriverWrench} />
                    <span className='font-semibold text-texts-secondary300 uppercase truncate'>
                        {SettingSections.MAINTENANCE}
                    </span>
                </div>
                {hasWindow && (
                    <Button
                        type='danger'
                        icon={faBan}
                        text='Cancel Window'
                        disabled={actionLoading}
                        onClick={handleCancel}
                    />
                )}
            </div>

            <div className='h-[calc(100vh-210px)] w-full overflow-auto px-6 space-y-6'>
                {loading ? (
                    <div className='flex items-center justify-center py-12 text-texts-secondary300'>
                        <FontAwesomeIcon icon={faSpinner} spin className='mr-2' />
                        Loading status…
                    </div>
                ) : (
                    <>
                        {/* ─── Current status card ───────────────────────── */}
                        <div className='rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden'>
                            <div className='flex items-center justify-between px-5 py-3 bg-backgrounds-primary100 border-b border-gray-200'>
                                <span className='font-semibold text-sm text-texts-secondary300'>Current Status</span>
                                <Button
                                    type='primary'
                                    icon={faRotate}
                                    text='Refresh'
                                    disabled={loading}
                                    onClick={fetchStatus}
                                />
                            </div>
                            <div className='px-5 py-4'>
                                {!sched || sched.status === 'completed' || sched.status === 'cancelled' ? (
                                    <div className='flex items-center gap-2 text-sm text-green-600'>
                                        <FontAwesomeIcon icon={faCircleCheck} />
                                        <span className='font-medium'>No maintenance window scheduled — app is running normally.</span>
                                    </div>
                                ) : (
                                    <div className='space-y-3'>
                                        <div className='flex items-center gap-3'>
                                            <StatusBadge status={sched.status} />
                                            {isActive && (
                                                <span className='flex items-center gap-1.5 text-xs text-red-500 font-semibold animate-pulse'>
                                                    <span className='w-2 h-2 rounded-full bg-red-500 inline-block' />
                                                    Caddy blocking traffic
                                                </span>
                                            )}
                                        </div>
                                        <div className='bg-gray-50 rounded-lg px-4 py-1'>
                                            <InfoRow label='Start'       value={fmtDateTime(sched.maintenanceStart)} />
                                            <InfoRow label='Approx. End' value={fmtDateTime(sched.maintenanceEnd)} />
                                            <InfoRow label='Duration'    value={`${sched.durationMinutes} minutes`} />
                                            {sched.note && <InfoRow label='Note' value={sched.note} />}
                                            <InfoRow label='Scheduled at' value={fmtDateTime(sched.scheduledAt)} />
                                            {sched.remindersSent?.length > 0 && (
                                                <InfoRow
                                                    label='Reminders sent'
                                                    value={`${sched.remindersSent.length} email${sched.remindersSent.length !== 1 ? 's' : ''}`}
                                                />
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* ─── Safety check panel (active only) ──────────── */}
                        {isActive && (
                            <div className={`rounded-xl border p-5 ${
                                jobCount === null ? 'bg-backgrounds-primary100 border-backgrounds-primary300'
                                : jobCount === 0  ? 'bg-green-50 border-green-200'
                                                  : 'bg-red-50 border-red-200'
                            }`}>
                                <div className='flex items-start gap-3 mb-4'>
                                    <FontAwesomeIcon
                                        icon={
                                            jobCount === null ? faCircleInfo
                                            : jobCount === 0  ? faCircleCheck
                                                              : faTriangleExclamation
                                        }
                                        className={`mt-0.5 text-lg ${
                                            jobCount === null ? 'text-texts-tertiary'
                                            : jobCount === 0  ? 'text-green-600'
                                                              : 'text-red-500'
                                        }`}
                                    />
                                    <div className='flex-1'>
                                        <p className={`font-semibold text-sm mb-1 ${
                                            jobCount === null ? 'text-texts-secondary300'
                                            : jobCount === 0  ? 'text-green-700'
                                                              : 'text-red-700'
                                        }`}>
                                            {jobCount === null
                                                ? 'Not yet checked — click below to run a safety check.'
                                                : jobCount === 0
                                                    ? '✅ No processing jobs found — safe to restart the server.'
                                                    : `🚫 ${jobCount} job${jobCount !== 1 ? 's' : ''} still PROCESSING — do NOT restart yet.`}
                                        </p>
                                        <p className='text-xs text-gray-500'>
                                            {jobCount === 0
                                                ? 'A safe-to-restart email has been sent to all super-admins.'
                                                : 'Auto-checked every 5 min. Super-admins are emailed when all jobs complete. Click below to re-check immediately.'}
                                        </p>
                                    </div>
                                </div>

                                <div className='flex flex-wrap items-center gap-3'>
                                    <Button
                                        type={jobCount === 0 ? 'success' : 'primary'}
                                        icon={actionLoading ? faSpinner : faShieldHalved}
                                        text={jobCount === 0 ? 'Re-check & Notify Admins' : 'Re-check Jobs Now'}
                                        disabled={actionLoading}
                                        onClick={handleConfirmShutdown}
                                    />
                                    {jobCount === 0 && (
                                        <span className='flex items-center gap-1.5 text-sm font-semibold text-green-700 bg-green-100 border border-green-300 px-3 py-1.5 rounded-lg'>
                                            <FontAwesomeIcon icon={faPowerOff} />
                                            Safe — restart via PM2 / SSH
                                        </span>
                                    )}
                                </div>

                                {/* Remove maintenance page — manual step to restore Caddy traffic */}
                                <div className='mt-4 pt-4 border-t border-gray-200 flex flex-wrap items-center gap-3'>
                                    <Button
                                        type='danger'
                                        icon={actionLoading ? faSpinner : faPlugCircleXmark}
                                        text='Remove Maintenance Page'
                                        disabled={actionLoading}
                                        onClick={handleRemovePage}
                                    />
                                    <p className='text-xs text-gray-500'>
                                        Removes <code className='bg-gray-100 px-1 rounded'>maintenance.json</code> so Caddy immediately restores traffic. Do this only after restarting services.
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* ─── Schedule form (only when no active window) ── */}
                        {!hasWindow && (
                            <div className='rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden'>
                                <div className='px-5 py-3 bg-backgrounds-primary100 border-b border-gray-200'>
                                    <span className='font-semibold text-sm text-texts-secondary300 flex items-center gap-2'>
                                        <FontAwesomeIcon icon={faCalendarPlus} />
                                        Schedule a Maintenance Window
                                    </span>
                                    <p className='text-xs text-gray-500 mt-1'>
                                        At the scheduled time Caddy automatically blocks all traffic and serves the maintenance page.
                                        All users are emailed immediately and reminded every 24 hours until the window begins.
                                    </p>
                                </div>
                                <form onSubmit={handleSchedule} className='px-5 py-5 space-y-5'>
                                    <div className='flex flex-col md:flex-row gap-5'>
                                        <div className='flex-1'>
                                            <label className='block text-sm font-medium text-texts-secondary300 mb-1.5'>
                                                Start Date &amp; Time <span className='text-red-500'>*</span>
                                            </label>
                                            <input
                                                type='datetime-local'
                                                min={minDateTimeLocal()}
                                                value={formStart}
                                                onChange={(e) => setFormStart(e.target.value)}
                                                required
                                                className='w-full px-3 py-2 rounded-xl border backgrounds-border text-sm focus:outline-none focus:border-backgrounds-primary300 focus:ring-1 focus:ring-backgrounds-primary200 hover:border-backgrounds-primary300'
                                            />
                                            <p className='text-xs text-gray-400 mt-1'>Minimum 1 hour from now</p>
                                        </div>
                                        <div className='w-full md:w-44'>
                                            <label className='block text-sm font-medium text-texts-secondary300 mb-1.5'>
                                                Duration (minutes) <span className='text-red-500'>*</span>
                                            </label>
                                            <input
                                                type='number'
                                                min={15}
                                                max={480}
                                                value={formDuration}
                                                onChange={(e) => setFormDuration(parseInt(e.target.value, 10))}
                                                required
                                                className='w-full px-3 py-2 rounded-xl border backgrounds-border text-sm focus:outline-none focus:border-backgrounds-primary300 focus:ring-1 focus:ring-backgrounds-primary200 hover:border-backgrounds-primary300'
                                            />
                                            <p className='text-xs text-gray-400 mt-1'>Default: 60 min</p>
                                        </div>
                                    </div>

                                    <div>
                                        <label className='block text-sm font-medium text-texts-secondary300 mb-1.5'>
                                            Note <span className='text-gray-400 font-normal'>(optional — shown in admin emails only)</span>
                                        </label>
                                        <Input
                                            placeholder='e.g. Database migration v2.3'
                                            value={formNote}
                                            onChange={(e) => setFormNote(e.target.value)}
                                            maxLength={200}
                                        />
                                    </div>

                                    <div className='flex items-center gap-4 pt-1'>
                                        <Button
                                            type='success'
                                            icon={faCalendarPlus}
                                            text='Schedule Maintenance'
                                            disabled={actionLoading}
                                            onClick={handleSchedule}
                                        />
                                        <p className='text-xs text-gray-400'>
                                            Immediately emails all verified users.
                                        </p>
                                    </div>
                                </form>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
